import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

const LegalLayout = ({ title, children }) => (
  <div className="min-h-screen bg-white">
    <header className="border-b sticky top-0 bg-white/90 backdrop-blur z-10">
      <div className="container mx-auto px-6 py-4 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <h1 className="font-heading text-xl font-bold ml-auto">{title}</h1>
      </div>
    </header>
    <main className="container mx-auto px-6 py-10 max-w-3xl prose prose-sm">
      {children}
    </main>
    <Footer />
  </div>
);

export const TermosPage = () => (
  <LegalLayout title="Termos de Uso">
    <p className="text-gray-500 text-xs mb-6">Última atualização: 03/05/2026</p>

    <h2 className="text-lg font-semibold mt-6 mb-2">1. Aceitação</h2>
    <p>
      Ao utilizar a plataforma GoFood, você concorda com estes Termos de Uso e com a Política de Privacidade.
      Se não concordar, não utilize a plataforma.
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">2. Cadastro</h2>
    <p>
      O cadastro exige dados completos, CPF/CNPJ válido, chave PIX para recebimentos/saques e aceite dos
      consentimentos LGPD. Você é responsável pela veracidade das informações fornecidas.
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">3. Tipos de Conta</h2>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Cliente:</strong> pode fazer pedidos e indicar outros usuários.</li>
      <li><strong>Lojista:</strong> assina plano mensal (R$ 199,00) e vende pela plataforma.</li>
      <li><strong>Entregador:</strong> realiza entregas (moto exige CNH A, bicicleta não exige CNH).</li>
    </ul>

    <h2 className="text-lg font-semibold mt-6 mb-2">4. Comissões & Rede MLM</h2>
    <p>
      A plataforma opera um programa de indicações multinível (5 níveis) sobre vendas, conforme configurado
      em <em>/admin/settings</em>. Comissões são creditadas em carteira interna e liberadas nas seguintes
      janelas:
    </p>
    <ul className="list-disc pl-5 space-y-1">
      <li>Operacionais (lojista + entregador): liberadas em até 48 horas úteis após entrega confirmada.</li>
      <li>MLM e bônus de indicação: liberados no 5º dia útil do mês seguinte.</li>
    </ul>

    <h2 className="text-lg font-semibold mt-6 mb-2">5. Pagamentos & Saques</h2>
    <p>
      Os pagamentos são processados via <strong>ASAAS</strong> (Instituição de Pagamento regulada pelo Banco
      Central). Saques são realizados por transferência PIX para a chave cadastrada, valor mínimo R$ 20,00.
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">6. Conduta</h2>
    <p>É vedado: uso fraudulento, criação de contas falsas, manipulação do programa de indicações.</p>

    <h2 className="text-lg font-semibold mt-6 mb-2">7. Suspensão & Cancelamento</h2>
    <p>
      A GoFood pode suspender ou encerrar contas que violem estes Termos ou a legislação aplicável, sem
      aviso prévio.
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">8. Contato</h2>
    <p>Dúvidas? Fale conosco pelos canais disponíveis no rodapé desta página.</p>

    <p className="text-xs text-gray-500 mt-10">
      Documento modelo para pré-lançamento. Revise com seu advogado antes de operar em produção com
      transações reais.
    </p>
  </LegalLayout>
);

export const PrivacidadePage = () => (
  <LegalLayout title="Política de Privacidade (LGPD)">
    <p className="text-gray-500 text-xs mb-6">Última atualização: 03/05/2026</p>

    <h2 className="text-lg font-semibold mt-6 mb-2">1. Quem somos</h2>
    <p>
      GoFood (nome fantasia) atua como controlador dos dados pessoais coletados nesta plataforma, nos
      termos da Lei 13.709/2018 (LGPD).
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">2. Dados coletados</h2>
    <ul className="list-disc pl-5 space-y-1">
      <li>Dados cadastrais: nome, e-mail, telefone, CPF/CNPJ, endereço.</li>
      <li>Dados financeiros: chave PIX, histórico de transações (tokenizado via ASAAS).</li>
      <li>Dados de entrega (geolocalização apenas enquanto a entrega está ativa).</li>
      <li>Dados operacionais: IP, dispositivo, logs de acesso.</li>
      <li>Entregadores de moto: número e foto da CNH (para validação).</li>
    </ul>

    <h2 className="text-lg font-semibold mt-6 mb-2">3. Bases legais</h2>
    <p>
      Executamos contrato (pedidos e pagamentos), cumprimento de obrigação legal (fiscal), consentimento
      (marketing) e legítimo interesse (prevenção a fraudes).
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">4. Compartilhamento</h2>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>ASAAS</strong>: processamento de pagamentos PIX, splits e transferências.</li>
      <li><strong>Lojistas e entregadores</strong>: apenas os dados necessários para preparar e entregar o pedido.</li>
      <li><strong>Autoridades</strong>: mediante requisição legal.</li>
    </ul>

    <h2 className="text-lg font-semibold mt-6 mb-2">5. Seus direitos (Art. 18 LGPD)</h2>
    <p>
      Você pode a qualquer momento solicitar: acesso, correção, anonimização, portabilidade, eliminação ou
      revogação de consentimento. Envie um e-mail para o contato do rodapé com o assunto "LGPD".
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">6. Segurança</h2>
    <p>
      Senhas são armazenadas com hash bcrypt. Comunicação via HTTPS. Dados financeiros sensíveis não são
      armazenados em nossos servidores (ASAAS).
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">7. Retenção</h2>
    <p>
      Dados são mantidos pelo tempo necessário ao cumprimento das finalidades e obrigações legais
      (geralmente 5 anos para registros fiscais).
    </p>

    <p className="text-xs text-gray-500 mt-10">
      Documento modelo para pré-lançamento. Revise com seu DPO/advogado.
    </p>
  </LegalLayout>
);

export const CookiesPage = () => (
  <LegalLayout title="Política de Cookies">
    <p className="text-gray-500 text-xs mb-6">Última atualização: 03/05/2026</p>
    <p>
      A GoFood utiliza cookies e tecnologias similares para oferecer uma melhor experiência, analisar o
      tráfego e personalizar o conteúdo.
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">Tipos de cookies</h2>
    <ul className="list-disc pl-5 space-y-1">
      <li><strong>Essenciais</strong>: necessários para o funcionamento (login, carrinho). Não podem ser desativados.</li>
      <li><strong>Analíticos</strong>: mensuram uso e desempenho da plataforma.</li>
      <li><strong>Funcionais</strong>: lembram preferências (idioma, tema).</li>
    </ul>

    <h2 className="text-lg font-semibold mt-6 mb-2">Armazenamento local</h2>
    <p>
      Utilizamos <code>localStorage</code> para manter a sessão (token JWT) e preferências. Você pode limpar
      pelo navegador a qualquer momento.
    </p>

    <h2 className="text-lg font-semibold mt-6 mb-2">Contato</h2>
    <p>Dúvidas sobre cookies? Utilize os canais do rodapé.</p>
  </LegalLayout>
);
